
import React, { useEffect } from 'react';
import { GenericScheduleProps, Schedule16RelatedPartyData, RelatedPartyTransactionItem, FormErrorsStep } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { safeParseFloat } from '../../utils/calculations';
import { translations } from '../../i18n/translations';


const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <h4 className="text-md font-semibold text-sky-700 mt-4 mb-2 border-b pb-1">{title}</h4>
);

export const Schedule16_1_RelatedParty: React.FC<GenericScheduleProps<Schedule16RelatedPartyData>> = ({ scheduleData, onChange, errors, t, fullFormData }) => {
  
  const handleItemChange = (index: number, field: keyof RelatedPartyTransactionItem, value: string) => {
    const updatedItems = scheduleData.transactions.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange('transactions', updatedItems);
  };

  const addItem = () => {
    onChange('transactions', [
      ...scheduleData.transactions,
      { 
        id: Date.now().toString(), 
        name: '', 
        transactionType: '', 
        taxResidence: '', 
        trnOrTin: '', 
        grossIncomeOrExpense: '', 
        transferPricingMethod: '', 
        armsLengthValue: '' 
      }
    ]);
  };

  const removeItem = (id: string) => {
    onChange('transactions', scheduleData.transactions.filter(item => item.id !== id));
  };

  const countryOptions = [
    { value: '', label: t('select_option') },
    { value: 'AE', label: t('country_ae') },
    { value: 'US', label: t('country_us') },
    { value: 'GB', label: t('country_gb') },
    { value: 'IN', label: t('country_in') },
  ];

  const transactionTypeOptions: {value: RelatedPartyTransactionItem['transactionType'], label: string}[] = [
    { value: '', label: t('select_option') },
    { value: 'goods', label: t('sch16_1_tt_goods') },
    { value: 'services', label: t('sch16_1_tt_services') },
    { value: 'intellectual_property', label: t('sch16_1_tt_ip') },
    { value: 'interest', label: t('sch16_1_tt_interest') },
    { value: 'assets', label: t('sch16_1_tt_assets') },
    { value: 'liabilities', label: t('sch16_1_tt_liabilities') },
    { value: 'other', label: t('sch16_1_tt_other') },
  ];

  const tpMethodOptions: {value: RelatedPartyTransactionItem['transferPricingMethod'], label: string}[] = [
    { value: '', label: t('select_option') },
    { value: 'cup', label: t('sch16_1_tp_cup') },
    { value: 'resale_price', label: t('sch16_1_tp_resale') },
    { value: 'cost_plus', label: t('sch16_1_tp_costplus') },
    { value: 'tnmm', label: t('sch16_1_tp_tnmm') },
    { value: 'profit_split', label: t('sch16_1_tp_profitsplit') },
    { value: 'other', label: t('sch16_1_tp_other') },
  ];

  useEffect(() => {
    let totalUpward = 0;
    let totalDownward = 0;

    const updatedTransactions = scheduleData.transactions.map(item => {
      const gross = safeParseFloat(item.grossIncomeOrExpense);
      const alv = safeParseFloat(item.armsLengthValue);
      const adjustment = gross - alv; // Positive is upward (increase TI), Negative is downward (decrease TI)
      
      if (adjustment > 0) { // If Gross > ALV, income understated or expense overstated -> upward adjustment
        totalUpward += adjustment;
      } else if (adjustment < 0) { // If Gross < ALV, income overstated or expense understated -> downward adjustment
        totalDownward += Math.abs(adjustment); 
      }
      return { ...item, taxAdjustment: adjustment.toFixed(2) };
    });

    if (JSON.stringify(updatedTransactions) !== JSON.stringify(scheduleData.transactions)) {
        onChange('transactions', updatedTransactions);
    }
    if (scheduleData.totalUpwardAdjustments !== totalUpward.toFixed(2)) {
        onChange('totalUpwardAdjustments', totalUpward.toFixed(2));
    }
    if (scheduleData.totalDownwardAdjustments !== totalDownward.toFixed(2)) {
        onChange('totalDownwardAdjustments', totalDownward.toFixed(2));
    }

  }, [scheduleData.transactions, onChange, scheduleData.totalUpwardAdjustments, scheduleData.totalDownwardAdjustments]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-sky-600">{t('sch16_1_title')}</h3>
      <p className="text-sm text-gray-600">{t('sch16_1_note_thresholds')}</p>
      
      {scheduleData.transactions.map((item, index) => (
        <div key={item.id} className="p-3 border rounded-md space-y-3 bg-slate-50 relative">
            <p className="text-sm font-semibold text-gray-600">{t('sch16_1_transaction_item_heading', {index: index + 1})}</p>
            
            <Input label={t('sch16_1_rp_name_label')} type="text" id={`s16rpName-${item.id}`} value={item.name} onChange={(e) => handleItemChange(index, 'name', e.target.value)} error={(errors?.transactions as FormErrorsStep[])?.[index]?.name as string}/>
            <Select label={t('sch16_1_transaction_type_label')} id={`s16rpTransType-${item.id}`} value={item.transactionType} options={transactionTypeOptions} onChange={(e) => handleItemChange(index, 'transactionType', e.target.value as RelatedPartyTransactionItem['transactionType'])} error={(errors?.transactions as FormErrorsStep[])?.[index]?.transactionType as string}/>
            <Select label={t('sch16_1_tax_residence_label')} id={`s16rpTaxRes-${item.id}`} value={item.taxResidence} options={countryOptions} onChange={(e) => handleItemChange(index, 'taxResidence', e.target.value)} error={(errors?.transactions as FormErrorsStep[])?.[index]?.taxResidence as string}/>
            <Input label={t('sch16_1_trn_tin_label')} type="text" id={`s16rpTrnTin-${item.id}`} value={item.trnOrTin} onChange={(e) => handleItemChange(index, 'trnOrTin', e.target.value)} error={(errors?.transactions as FormErrorsStep[])?.[index]?.trnOrTin as string}/>
            <Input label={t('sch16_1_gross_income_expense_label')} type="number" id={`s16rpGrossIncExp-${item.id}`} value={item.grossIncomeOrExpense} onChange={(e) => handleItemChange(index, 'grossIncomeOrExpense', e.target.value)} error={(errors?.transactions as FormErrorsStep[])?.[index]?.grossIncomeOrExpense as string}/>
            <Select label={t('sch16_1_tp_method_label')} id={`s16rpTpMethod-${item.id}`} value={item.transferPricingMethod} options={tpMethodOptions} onChange={(e) => handleItemChange(index, 'transferPricingMethod', e.target.value as RelatedPartyTransactionItem['transferPricingMethod'])} error={(errors?.transactions as FormErrorsStep[])?.[index]?.transferPricingMethod as string}/>
            {item.transferPricingMethod === 'other' && (
                 <Input label={t('sch16_1_other_method_desc_label')} type="text" id={`s16rpOtherMethodDesc-${item.id}`} value={item.otherMethodDescription || ''} onChange={(e) => handleItemChange(index, 'otherMethodDescription', e.target.value)} error={(errors?.transactions as FormErrorsStep[])?.[index]?.otherMethodDescription as string}/>
            )}
            <Input label={t('sch16_1_arms_length_value_label')} type="number" id={`s16rpAlv-${item.id}`} value={item.armsLengthValue} onChange={(e) => handleItemChange(index, 'armsLengthValue', e.target.value)} error={(errors?.transactions as FormErrorsStep[])?.[index]?.armsLengthValue as string}/>
            <Input label={t('sch16_1_tax_adjustment_label')} type="text" id={`s16rpTaxAdj-${item.id}`} value={item.taxAdjustment || '0.00'} readOnly className="bg-gray-100" helpText={t('sch16_1_tax_adjustment_help')}/>

            <Button onClick={() => removeItem(item.id)} variant="danger_outline" size="sm" className="absolute top-2 right-2">{t('remove_item_button')}</Button>
        </div>
      ))}
      <Button onClick={addItem} variant="secondary" size="sm">{t('sch16_1_add_transaction_button')}</Button>

      <SectionHeader title={t('sch16_1_summary_adjustments_title')} />
      <Input label={t('sch16_1_total_upward_adjustments_label')} type="text" id="s16TotalUpwardAdj" value={scheduleData.totalUpwardAdjustments || '0.00'} readOnly className="bg-gray-100 font-semibold" />
      <Input label={t('sch16_1_total_downward_adjustments_label')} type="text" id="s16TotalDownwardAdj" value={scheduleData.totalDownwardAdjustments || '0.00'} readOnly className="bg-gray-100 font-semibold" helpText={t('sch16_1_downward_adjustment_note')}/>
    </div>
  );
};
